import { Icons, Images } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import SkeletonLoader from "shared/loaders/skeleton";
import ConfirmationModal from "shared/modal/confirmation";
import {
  GetAllUsersService,
  SuspendUserService,
  UnFlagUserService,
  UnSuspendUserService,
} from "shared/services/users";
import { handleErrors } from "shared/utils/helper";
import styles from "./style.module.scss";

interface UsersData {
  id: number;
  profile_image: {
    fullpath: string;
  };
  name: string;
  email: string;
  address: string;
  status: string;
  is_suspended: boolean;
  flagged: boolean;
  flagged_comment: string;
}
const DashboardFlaggedUsers = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [users, setUsers] = useState<UsersData[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selectedUser, setSelectedUser] = useState<UsersData>();
  const [suspendLoading, setSuspendLoading] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [isFlagConfirmation, setIsFlagConfirmation] = useState<boolean>(false);

  const handleShowConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const tableHeads = [
    { title: "Profile Image", key: "img" },
    { title: "Name", key: "name" },
    { title: "Email", key: "email" },
    { title: "Address", key: "address" },
    { title: "Reason", key: "reason" },
    { title: "Status", key: "status" },
    { title: "Action", key: "action" },
  ];

  const handleGetAllUsers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await GetAllUsersService(page, 10, true);
      const fetchedUsers = response.data.data.data || [];
      setUsers(fetchedUsers);
      setUsers(response.data.data.data || []);

      setTotalItems(response.data.data.meta.totalItems || 0);
    } catch (error) {
      console.error("Error fetching users:", error);
      toastMessage("error", "Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendUser = async () => {
    if (!selectedUser) return;

    setSuspendLoading(true);
    if (selectedUser?.is_suspended) {
      UnSuspendUserService(selectedUser.id)
        .then(({ data, status }) => {
          if (status) {
            toastMessage("success", "User Activated Successfully");
            handleGetAllUsers(currentPage);
            handleCloseConfirmationModal();
          }
        })
        .catch((error) => {
          handleErrors(error);
        })
        .finally(() => {
          setSuspendLoading(false);
        });
    } else {
      SuspendUserService(selectedUser.id)
        .then(({ data, status }) => {
          if (status) {
            toastMessage("success", "User Suspended Successfully");
            handleGetAllUsers(currentPage);
            handleCloseConfirmationModal();
          }
        })
        .catch((error) => {
          handleErrors(error);
        })
        .finally(() => {
          setSuspendLoading(false);
        });
    }
  };

  const handleFlagUser = async () => {
    if (!selectedUser) return;
    setSuspendLoading(true);
    UnFlagUserService(selectedUser.id)
      .then(({ data, status }) => {
        if (status) {
          toastMessage("success", "User Unflagged Successfully");
          handleGetAllUsers(currentPage);
          handleCloseConfirmationModal();
        }
      })
      .catch((error) => {
        handleErrors(error);
      })
      .finally(() => {
        setSuspendLoading(false);
      });
  };

  useEffect(() => {
    handleGetAllUsers(currentPage);
  }, [currentPage]);

  return (
    <div className={styles.sectionContainer}>
      <div className={classNames(styles.tableWrapper, "relative")}>
        <CustomTable
          heads={tableHeads}
          title="Flagged Users"
          isEmpty={!loading && !users.length}
        >
          {loading
            ? Array.from({ length: 2 }).map((_, index) => (
                <TableRowLoader key={index} />
              ))
            : users.map((data, idx) => (
                <tr key={data.id}>
                  <td>
                    <img
                      src={data.profile_image?.fullpath || Images.DefaultAvatar}
                      alt=""
                    />
                  </td>
                  <td>{data.name}</td>
                  <td>{data.email}</td>
                  <td>{data.address}</td>
                  <td>{data.flagged_comment}</td>
                  <td>{data.is_suspended ? "Suspended" : "Active"}</td>
                  <td className="">
                    <div className={classNames(styles.IconsContainer)}>
                      <CustomToolTip label="View">
                        <div
                          onClick={() => {
                            window.open(
                              `https://isopowersportsparts.com/general/seller/${data.id}`,
                              "_blank"
                            );
                          }}
                          className={classNames(styles.actionContainer)}
                        >
                          <Icons.Eye />
                        </div>
                      </CustomToolTip>
                      {data.is_suspended ? (
                        <CustomToolTip label="Activate">
                          <div
                            onClick={() => {
                              setIsFlagConfirmation(false);
                              setSelectedUser(data);
                              handleShowConfirmationModal();
                            }}
                            className={classNames(styles.actionContainer)}
                          >
                            <Icons.Tick className={classNames(styles.accept)} />
                          </div>
                        </CustomToolTip>
                      ) : (
                        <CustomToolTip label="Suspend">
                          <div
                            onClick={() => {
                              setIsFlagConfirmation(false);
                              setSelectedUser(data);
                              handleShowConfirmationModal();
                            }}
                            className={classNames(styles.actionContainer)}
                          >
                            <Icons.Cross />
                          </div>
                        </CustomToolTip>
                      )}
                      <CustomToolTip label="Unflag">
                        <div
                          onClick={() => {
                            setIsFlagConfirmation(true);
                            setSelectedUser(data);
                            handleShowConfirmationModal();
                          }}
                          className={classNames(styles.actionContainer)}
                        >
                          <Icons.FlagFill />
                        </div>
                      </CustomToolTip>
                    </div>
                  </td>
                </tr>
              ))}
        </CustomTable>
      </div>

      <CustomPagination
        onPageChange={handlePageChange}
        totalCount={totalItems}
        currentPage={currentPage}
        pageSize={10}
      />

      <ConfirmationModal
        show={showConfirmationModal}
        handleClose={handleCloseConfirmationModal}
        handleSubmit={() => {
          if (isFlagConfirmation) {
            handleFlagUser();
          } else {
            handleSuspendUser();
          }
        }}
        loading={suspendLoading}
      />
    </div>
  );
};

const TableRowLoader = () => (
  <tr>
    {Array.from({ length: 7 }).map((_, index) => (
      <td
        key={index}
        className={classNames(styles.tableContentLabel, "px-2 py-3")}
      >
        <div className="flex items-center justify-center">
          <SkeletonLoader className={styles.lineLoader} />
        </div>
      </td>
    ))}
  </tr>
);

export default DashboardFlaggedUsers;
